import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useClientProjectStore } from '@/stores/service-provider/client-project-store';
import { useEngagementStore } from '@/stores/service-provider/engagement-store';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import EngagementTimeline from '@/components/service-provider/EngagementTimeline';
import type { Specialization } from '@/types';
import * as errorUtils from '@/utils/errors';

const STATUS_COLORS: Record<string, string> = {
  setup: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  configured: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  handoff_pending: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  archived: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
};

const SPECIALIZATION_OPTIONS: { value: Specialization; label: string }[] = [
  { value: 'electrical' as Specialization, label: 'Electrical' },
  { value: 'plumbing' as Specialization, label: 'Plumbing' },
  { value: 'hvac' as Specialization, label: 'HVAC' },
  { value: 'security' as Specialization, label: 'Security' },
  { value: 'networking' as Specialization, label: 'Networking' },
  { value: 'solar' as Specialization, label: 'Solar' },
  { value: 'av' as Specialization, label: 'A/V' },
  { value: 'general' as Specialization, label: 'General' },
];

const ClientProjectDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { currentClientProject, isLoading: projectLoading, error: projectError, getClientProject } = useClientProjectStore();
  const {
    engagements,
    isLoading: engagementLoading,
    fetchEngagements,
    createEngagement,
    completeEngagement,
    revokeEngagement,
  } = useEngagementStore();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({
    serviceProviderEmail: '',
    specializationScope: 'general' as Specialization,
    endDate: '',
  });
  const [inviteError, setInviteError] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  useEffect(() => {
    if (projectId) {
      getClientProject(projectId);
      fetchEngagements(projectId);
    }
  }, [projectId, getClientProject, fetchEngagements]);

  const handleInviteServiceProvider = async () => {
    if (!projectId || !inviteData.serviceProviderEmail || !inviteData.endDate) return;
    setInviteError('');
    setInviteLoading(true);

    try {
      await createEngagement(projectId, {
        specializationScope: inviteData.specializationScope,
        serviceProviderEmail: inviteData.serviceProviderEmail,
        accessWindow: {
          startDate: new Date().toISOString(),
          endDate: new Date(inviteData.endDate).toISOString(),
        },
      });
      setShowInviteModal(false);
      setInviteData({ serviceProviderEmail: '', specializationScope: 'general' as Specialization, endDate: '' });
      fetchEngagements(projectId);
    } catch (error: unknown) {
      setInviteError(errorUtils.getApiErrorMessage(error, 'Failed to create engagement'));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCompleteEngagement = async (engagementId: string) => {
    setActionLoadingId(engagementId);
    try {
      await completeEngagement(engagementId);
      if (projectId) fetchEngagements(projectId);
    } catch {
      // Error tracked in store
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRevokeEngagement = async (engagementId: string) => {
    setActionLoadingId(engagementId);
    try {
      await revokeEngagement(engagementId);
      if (projectId) fetchEngagements(projectId);
    } catch {
      // Error tracked in store
    } finally {
      setActionLoadingId(null);
    }
  };

  if (projectLoading && !currentClientProject) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-muted-foreground">Loading client project...</div>
      </div>
    );
  }

  if (projectError) {
    return (
      <div className="mx-auto max-w-4xl py-8 px-4">
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          {projectError}
        </div>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/service-provider/dashboard')}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  if (!currentClientProject) return null;

  const address = [currentClientProject.address?.street, currentClientProject.address?.city, currentClientProject.address?.state, currentClientProject.address?.postalCode]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="mx-auto max-w-4xl py-8 px-4 space-y-6">
      {/* Client Project Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{currentClientProject.name}</h1>
          {address && <p className="text-muted-foreground mt-1">{address}</p>}
          <div className="flex items-center gap-2 mt-2">
            <Badge className={STATUS_COLORS[currentClientProject.status] || ''}>
              {currentClientProject.status}
            </Badge>
            <span className="text-sm text-muted-foreground capitalize">{currentClientProject.projectType}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {currentClientProject.linkedProjects?.[0]?.projectId && (
            <Button onClick={() => navigate(`/editor/${currentClientProject.linkedProjects[0].projectId}`)}>
              Open Editor
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/service-provider/dashboard')}>
            Back
          </Button>
        </div>
      </div>

      {/* Customer Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Customer</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-1">
          <div><span className="text-muted-foreground">Email:</span> {currentClientProject.customerEmail}</div>
          {currentClientProject.customerName && (
            <div><span className="text-muted-foreground">Name:</span> {currentClientProject.customerName}</div>
          )}
        </CardContent>
      </Card>

      {/* Engagements */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Engagements</CardTitle>
            <CardDescription>Specialization-scoped work authorizations</CardDescription>
          </div>
          <Button size="sm" onClick={() => setShowInviteModal(true)}>
            Invite Service Provider
          </Button>
        </CardHeader>
        <CardContent>
          {engagementLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : (
            <>
              <EngagementTimeline engagements={engagements} />
              {/* Action buttons for active engagements */}
              {engagements.filter((e) => e.status === 'active' || e.status === 'pending').length > 0 && (
                <div className="mt-4 space-y-2">
                  {engagements
                    .filter((e) => e.status === 'active' || e.status === 'pending')
                    .map((e) => (
                      <div key={e._id} className="flex items-center justify-between p-2 rounded bg-muted/50">
                        <span className="text-sm">{e.specializationScope} — {e.status}</span>
                        <div className="flex gap-2">
                          {e.status === 'active' && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={actionLoadingId === e._id}
                              onClick={() => handleCompleteEngagement(e._id)}
                            >
                              {actionLoadingId === e._id ? 'Completing...' : 'Complete'}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={actionLoadingId === e._id}
                            onClick={() => handleRevokeEngagement(e._id)}
                          >
                            {actionLoadingId === e._id ? 'Revoking...' : 'Revoke'}
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Handoff (Phase 3 stub) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Customer Handoff</CardTitle>
          <CardDescription>Transfer project ownership to the customer</CardDescription>
        </CardHeader>
        <CardContent>
          <Button disabled title="Coming in Phase 3">
            Hand Off to Customer
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            Customer handoff will be available in Phase 3.
          </p>
        </CardContent>
      </Card>

      {/* Invite Service Provider Modal */}
      {showInviteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="invite-modal-title"
          onClick={(e) => { if (e.target === e.currentTarget) setShowInviteModal(false); }}
        >
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle id="invite-modal-title">Invite Service Provider</CardTitle>
              <CardDescription>Add a specialist to this client project</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {inviteError && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
                  {inviteError}
                </div>
              )}
              <div>
                <Label htmlFor="inviteEmail">Service Provider Email *</Label>
                <Input
                  id="inviteEmail"
                  type="email"
                  value={inviteData.serviceProviderEmail}
                  onChange={(e) => setInviteData((d) => ({ ...d, serviceProviderEmail: e.target.value }))}
                  placeholder="provider@company.com"
                />
              </div>
              <div>
                <Label htmlFor="inviteSpecialization">Specialization Scope *</Label>
                <select
                  id="inviteSpecialization"
                  value={inviteData.specializationScope}
                  onChange={(e) => setInviteData((d) => ({ ...d, specializationScope: e.target.value as Specialization }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {SPECIALIZATION_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="inviteEndDate">Access End Date *</Label>
                <Input
                  id="inviteEndDate"
                  type="date"
                  value={inviteData.endDate}
                  onChange={(e) => setInviteData((d) => ({ ...d, endDate: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowInviteModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleInviteServiceProvider}
                  disabled={inviteLoading || !inviteData.serviceProviderEmail || !inviteData.endDate}
                >
                  {inviteLoading ? 'Inviting...' : 'Send Invitation'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ClientProjectDetailPage;
